import { useState, useEffect } from 'react';
import { Globe, Check, ChevronDown, MapPin } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useLanguage, LANGUAGES, SEARCH_COUNTRIES } from '../contexts/LanguageContext';

// Re-export for backward compatibility
export { LANGUAGES };

// Get current language from localStorage (for non-context usage)
export const getCurrentLanguage = () => {
  const saved = localStorage.getItem('baremc-language');
  return saved || 'da';
};

// Language selector component
const LanguageSelector = ({ compact = false, className = '' }) => {
  const { language, setLanguage, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const handleSelectLanguage = (code) => {
    setLanguage(code);
    setIsOpen(false);
  };

  const currentLanguage = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className={`flex items-center gap-1 px-2 py-1.5 rounded-lg transition-all hover:bg-white/10 touch-manipulation ${className}`}
          data-testid="language-selector"
        >
          <span className="text-xl leading-none">{currentLanguage.flag}</span>
          {compact ? (
            <ChevronDown className={`w-3 h-3 text-white/40 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          ) : (
            <>
              <span className="text-cream text-sm">{currentLanguage.name}</span>
              <ChevronDown className={`w-4 h-4 text-cream-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-48 bg-zinc-900 border-white/20"
      >
        <div className="px-3 py-2 border-b border-white/10">
          <div className="flex items-center gap-2 text-cream-muted text-xs font-medium">
            <Globe className="w-3.5 h-3.5" />
            {t('account.language')}
          </div>
        </div>
        {LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleSelectLanguage(lang.code)}
            className={`cursor-pointer py-2.5 px-3 ${
              lang.code === language ? 'bg-white/10' : ''
            }`}
          >
            <span className="text-xl mr-3 leading-none">{lang.flag}</span>
            <span className="flex-1 text-cream">{lang.name}</span>
            {lang.code === language && (
              <Check className="w-4 h-4 text-emerald-400" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Search Country Selector Component
export const SearchCountrySelector = ({ compact = false, className = '' }) => {
  const { searchCountry, setSearchCountry, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const handleSelectCountry = (code) => {
    setSearchCountry(code);
    setIsOpen(false);
  };

  const currentCountry = SEARCH_COUNTRIES.find(c => c.code === searchCountry) || SEARCH_COUNTRIES[0];

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all hover:bg-white/10 border border-white/10 ${className}`}
          data-testid="country-selector"
        >
          <span className="text-xl leading-none">{currentCountry.flag}</span>
          {!compact && (
            <>
              <span className="text-cream text-sm">{t(`countries.${currentCountry.nameKey}`)}</span>
              <ChevronDown className={`w-4 h-4 text-cream-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-52 bg-zinc-900 border-white/20"
      >
        <div className="px-3 py-2 border-b border-white/10">
          <div className="flex items-center gap-2 text-cream-muted text-xs font-medium">
            <MapPin className="w-3.5 h-3.5" />
            {t('account.searchCountry')}
          </div>
        </div>
        {SEARCH_COUNTRIES.map((country) => (
          <DropdownMenuItem
            key={country.code}
            onClick={() => handleSelectCountry(country.code)}
            className={`cursor-pointer py-2.5 px-3 ${
              country.code === searchCountry ? 'bg-white/10' : ''
            }`}
          >
            <span className="text-xl mr-3 leading-none">{country.flag}</span>
            <span className="flex-1 text-cream">{t(`countries.${country.nameKey}`)}</span>
            {country.code === searchCountry && (
              <Check className="w-4 h-4 text-emerald-400" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
